import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Reusable searchable dropdown. Behaves like a native <select> (same
// value/onChange contract: onChange receives the plain string value, or
// "" when cleared) but lets the user type to filter the option list -
// handy for long lists like Product, Person, and Machine.
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  style,
  className,
}) => {
  const [query, setQuery] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);

  // Keep the visible text in sync whenever the selected value changes
  // from outside (e.g. sessionStorage restore, mutual-exclusion resets).
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close the dropdown when clicking anywhere outside it, and snap the
  // typed text back to the actual selected value.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery(value || "");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (opt) => {
    onChange(opt);
    setQuery(opt);
    setIsOpen(false);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setHighlightIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredOptions[highlightIndex]) {
        handleSelect(filteredOptions[highlightIndex]);
      } else if (filteredOptions.length === 1) {
        handleSelect(filteredOptions[0]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery(value || "");
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        className={className}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          const typed = e.target.value;
          setQuery(typed);
          setIsOpen(true);
          setHighlightIndex(-1);
          if (typed === "") {
            onChange("");
          }
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{ ...style, textTransform: "uppercase" }}
      />

      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            maxHeight: "260px",
            overflowY: "auto",
            background: "#ffffff",
            border: "2px solid #d1d5db",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            zIndex: 50,
          }}
        >
          {filteredOptions.length === 0 && (
            <div
              style={{
                padding: "12px 16px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              No matches found
            </div>
          )}

          {filteredOptions.map((opt, index) => (
            <div
              key={index}
              onMouseDown={(e) => {
                // onMouseDown (not onClick) so this fires before the
                // input's onBlur/close-on-outside-click logic.
                e.preventDefault();
                handleSelect(opt);
              }}
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                color: "#000",
                fontSize: "clamp(14px, 2vw, 18px)",
                fontWeight: opt === value ? "700" : "400",
                textTransform: "uppercase",
                background:
                  index === highlightIndex
                    ? "#e5e7eb"
                    : opt === value
                    ? "#f3f4f6"
                    : "#fff",
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Hydraulics = () => {
  const navigate = useNavigate();

  const DEFAULT_PRODUCTS = [
    "HYDAX 19",
    "HYDAX 28",
    "HYDAX 38",
    "HYDAX 48",
    "HYDAX 55",
    "HYDAX 65",

    "BT 250 UM 40 1/2\"BSP",
    "CRLF - 04 - 10",
    "CRLF - 04 - 25",
    "CRLF - 06 - 25",
    "CRLF - 08 - 10",
    "CRLF - 08 - 25",
    "CRLF - 10 - 10",
    "CRLF - 10 - 25",
    "CRLF - 12 - 10",
    "CRLF - 12 - 25",
    "FB 250 SS FLANGE",
    "FB 250 TT 40 (SS STRAINER)",
    "FB 700 SS FLANGE",
    "FB 700 TT 40",
    "FILLER BREATHER - FB 250 TT 40",
    "HPLF-10-25",
    "RLF-04-10-TT",
    "RLF-04-10-TT-VI (1/8\" BSPT)",
    "RLF-04-10-TT-VI (1/8\" NPT)",
    "RLF-04-25-TT",
    "RLF-04-25-TT-VI (1/8\" BSPT)",
    "RLF-04-25-TT-VI (1/8\" BSPT) ( W/O VI)",
    "RLF-04-25-TT-VI (1/8\" NPT)",
    "RLF-06-25-TT",
    "RLF-06-25-TT-VI (1/8\" BSPT)",
    "RLF-06-25-TT-VI (1/8\" NPT)",
    "RLF-08-10-TT",
    "RLF-08-10-TT-VI (1/8\" BSPT)",
    "RLF-08-10-TT-VI (1/8\" NPT)",
    "RLF-08-25-TT",
    "RLF-08-25-TT-VI (1/8\" BSPT)",
    "RLF-08-25-TT-VI (1/8\" NPT)",
    "RLF-10-25-TT",
    "RLF-10-25-TT-VI (1/8\" NPT)",
    "RLF-12-10-TT-VI (1/8\" BSPT)",
    "RLF-12-25-TT",
    "RLF-12-25-TT-VI (1/8\" BSPT)",
    "RLF-12-25-TT-VI (1/8\" NPT)",
    "SGR 140MM",
    "SGR 140MM ASSEMBLY",
    "SGR 300MM",
    "SGR 300MM ASSEMBLY",
    "SGR-03",
    "SGR-05",
    "SGR-10",
    "SGR-14",
    "SG-03",
    "SG-05",
    "SGR-14 SS",
    "SGR-10 SS",
    "SGR-10 ASSEMBLY",
    "SPARE HUB - 19",
    "SPARE SLEEVE - 19",
    'SS6041/ 90 LPM 1" BSPT',
    'SS125901  1/2" BSP-P',
    'SS1552 1/2" BSP-P',
    "SS15633/4\"BSP-P",
    'SS160901 1/2" BSP-P',
    "SS200902 BSP-P",
    "SS2501402 BSP-M",
    'SS250902" BSP-P',
    'SS3001402  1/2" BSP-M',
    "SS30633/4\" BSP-P",
    "SS34014021/2\" BSP-M",
    "SS4501403 BSP-M",
    "SS6063 1\" BSP-P",
    'SS90901 1/2" BSP-P',
    "84 D 3/4\" BSP NUT 190mm LONG DOOM CAP FILTER",
    "84 D 3/4\" BSP NUT 290mm LONG WITH DOOM CAP FILTER",
    "AB 50 40",
    "AB 700 TT 40 GDMHE",
    "ACGA 1/4\" BSP",
    "ACGA 5/8\" UNF",
    "ATOM SR (COOLANT FILTRATION-B)",
    "ATOM-J (COOLANT FILTRATION MACHINE)",
    "BAYONET- FB 250",
    "BINATONE AIRLINK HOTSPOT",
    "BT 700 TT 40 1\"BSP",
    "BT 700 TT 40 M30",
    "BT 700 UM 40 M 34",
    "BT-250 TT 40 M10",
    "BT-250 TT 40 M10 ASSEMBLY",
    "BT-250 TT 40 M12",
    "BT-250 UM 40 1/4\"BSP",
    "BT-700 UM 40 1\"",
    "BT-700 UM 40 1/2\"",
    "BT-700 UM 40 3/4\"BSP",
  ];

  const DEFAULT_PERSONS = [
    "Uday",
    "Appudev",
    "Pravasini",
    "Sidharaju",
    "Chaithra",
    "Lilli",
    "Moni",
    "Sheela",
    "Krishnamoorthy",
    "Bikash",
  ];

  const DEFAULT_MACHINES = [
    "SPM CUTTING MACHINE",
    "LATHE",
    "JYOTI CNC TURNING CENTER1",
    "JYOTI CNC TURNING CENTER2",
    "PAWAN CNC TURNING CENTER",
    "DEMM-1",
    "EIFCO",
    "LASER MARKING MACHINE",
    "LIGHT TESTING",
    "ASSEMBLY TABLE 1",
    "DRILLING MACHINE",
    "HORIZONTAL BANDSAW CUTTING MACHINE",
    "SPOT WELDING MACHINE",
    "SS WELDING MACHINE",
    "PLEATING MACHINE",
    "MESH CUTTING MACHINE",
    "RLF BODY AND COVER LEAK TESTING MACHINE",
    "SURFACE PLATE",
    "ECM DEBURRING MACHINE",
    "VALVE LEAK TESTING MACHIE",
    "HONING MACHINE",
    "DRILLING MACHINE(SMALL)",
    "MILLING MACHINE (SUB CONTRACTOR)",
    "LAPPING MACHINE",
    "TABLE SAW CUTTING MACHINE",
    "GENERATOR",
    "LEAK TESTING MACHINE",
    "ASSEMBLY TABLE 2",
    "RLF BODY TAPPING MACHINE",
    "RLF BODY BOX FACING MACHINE",
    "HAND TAPPING MACHINE",
    "SUB-CONTRACTOR MACHINE",
    "MALWA CUTTING MACHINE",
    "PLASTIC MOULDING MACHINE",
    "GISSPR PAINT BOOTH",
    "DEMM-2",
    
 
  ];

  // --- Persistent (add/remove-able) dropdown options -----------------
  // Product/Person/Machine lists start from the hardcoded defaults above,
  // but the user can permanently add or remove entries via the ADD/REMOVE
  // buttons under each dropdown. Those changes are saved to localStorage
  // (NOT sessionStorage, so they survive closing the tab/browser, not just
  // a reload) and are re-loaded every time this page mounts.
  const STORAGE_KEYS = {
    products: "hydraulics_products_v1",
    persons: "hydraulics_persons_v1",
    machines: "hydraulics_machines_v1",
    shifts: "hydraulics_shifts_v1",
  };

  // Default shifts now include a 4th shift alongside the original three
  // plus General. This is only the starting point - Team Leader/HR can
  // still ADD further shifts or REMOVE these via the buttons under the
  // Shift Name dropdown.
  const DEFAULT_SHIFTS = ["1st", "2nd", "3rd", "4th", "General"];

  // --- Cross-device sync (pure frontend, via JSONBin.io) -----------------
  // localStorage only lives on ONE browser/device, so an ADD or REMOVE
  // done on a laptop was never visible on a phone (and vice-versa). To fix
  // that WITHOUT writing any custom backend route, the Product/Person/
  // Machine/Shift lists are now stored in a single free JSONBin.io "bin"
  // (a hosted JSON file reachable over plain fetch()). Every add/remove
  // pushes the full updated set of lists to that bin, and the current
  // lists are pulled from the bin whenever this page loads - so every
  // device/browser that opens this page sees the same options.
  // localStorage is kept only as a same-device fallback/cache in case the
  // network/JSONBin is briefly unavailable.
  //
  // SETUP (one-time, ~2 minutes):
  //   1. Go to https://jsonbin.io and create a free account.
  //   2. Dashboard -> "Create Bin" -> paste this as the starting content:
  //        {"products": [], "persons": [], "machines": [], "shifts": []}
  //      Save it, then copy the Bin ID shown in the bin's URL
  //      (looks like 64f1a2b3c9e77b1a2c3d4e5f).
  //   3. Dashboard -> "API Keys" -> copy your X-Master-Key.
  //   4. Paste both values into JSONBIN_BIN_ID and JSONBIN_API_KEY below.
  // That's it - no server code needed. Every user's browser now reads
  // from and writes to the same shared bin directly.
  const JSONBIN_BIN_ID = "6a916555f5f4af5e294d64c5";
  const JSONBIN_API_KEY = "$2a$10$q5yFzvEqzU8NYKWFheK9S.lfRU2FwruXXyEdcgjHZZhU.S2mMvjQK";
  const JSONBIN_BASE_URL = `https://api.jsonbin.io/v3/b/${JSONBIN_BIN_ID}`;

  // JSONBin has no "add one item" / "remove one item" endpoint - each
  // write replaces the whole bin content. So every add/remove builds the
  // complete { products, persons, machines, shifts } object (the changed
  // list plus whatever the other three currently are) and PUTs that whole
  // object back. currentLists lets each caller pass in the four lists as
  // they stand right now (with the just-changed one already swapped in).
  const pushOptionsToServer = async (currentLists) => {
    try {
      const response = await fetch(JSONBIN_BASE_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Master-Key": JSONBIN_API_KEY,
        },
        body: JSON.stringify(currentLists),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "JSONBin rejected the change");
      }
      return true;
    } catch (e) {
      // Saved locally on this device, but the shared bin couldn't be
      // reached so other devices won't see it yet. We no longer show this
      // as a popup to the user - just log it for debugging - so the
      // person only ever sees the "Added successfully" / "Removed
      // successfully" confirmation.
      console.error("Failed to sync option change to JSONBin:", e);
      return false;
    }
  };

  // --- One-time product renames -----------------------------------------
  // Whenever a product's display name changes in code (e.g. adding the
  // missing "1/2\"" to its label), any OLD cached copy of the product list
  // - sitting in this browser's localStorage, or already stored on the
  // shared backend - would otherwise keep showing the stale name forever,
  // since loadOptions()/fetchServerOptions() both take an existing saved
  // list over the hardcoded DEFAULT_PRODUCTS. To avoid having to manually
  // clear localStorage or edit the backend every time a name changes, any
  // list we load (from localStorage OR the server) is passed through this
  // map first: old label -> new label. Add a new "old: new" line here
  // whenever a product name is renamed in DEFAULT_PRODUCTS/hydaxOperations.
  const PRODUCT_NAME_MIGRATIONS = {
    "SS125901  BSP-P": 'SS125901  1/2" BSP-P',
    "SS3001402  BSP-M": 'SS3001402  1/2" BSP-M',
    "SS1552 1/2 BSP-P": 'SS1552 1/2" BSP-P',
    "SS9090 1   BSP - P": 'SS90901 1/2" BSP-P',
    'SS90901 1/4" BSP-P': 'SS90901 1/2" BSP-P',
    "SS160901  BSP-P": 'SS160901 1/2" BSP-P',
    'SS 6041/ 90 LPM 1" BSPT': 'SS6041/ 90 LPM 1" BSPT',
    "SS250902 BSP-P": 'SS250902" BSP-P',
  };

  const migrateProductNames = (list) => {
    if (!Array.isArray(list)) return list;
    const migrated = list.map((item) =>
      Object.prototype.hasOwnProperty.call(PRODUCT_NAME_MIGRATIONS, item)
        ? PRODUCT_NAME_MIGRATIONS[item]
        : item
    );
    // De-dupe: if an old cached list had duplicate entries (e.g. the same
    // product listed twice) they'd otherwise migrate into two identical
    // new entries.
    return migrated.filter((item, index) => migrated.indexOf(item) === index);
  };

  // Some product lists come from a previously-saved localStorage cache or
  // from the shared server, both of which were populated BEFORE this
  // product existed. migrateProductNames() can only rename an entry that's
  // already in the list - it can't add a brand new one. So any product
  // that must always be selectable (even on an old cached/server list)
  // gets added here, and every loaded list is passed through this "ensure"
  // step right after migration.
  const ENSURE_PRODUCTS_PRESENT = ['SS1552 1/2" BSP-P', 'SS90901 1/2" BSP-P', 'SS160901 1/2" BSP-P', 'SS6041/ 90 LPM 1" BSPT', 'SS250902" BSP-P'];

  const ensureProductsPresent = (list) => {
    if (!Array.isArray(list)) return list;
    const result = [...list];
    ENSURE_PRODUCTS_PRESENT.forEach((product) => {
      if (!result.includes(product)) {
        result.push(product);
      }
    });
    return result;
  };

  const loadOptions = (key, defaults) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return key === STORAGE_KEYS.products
            ? ensureProductsPresent(migrateProductNames(parsed))
            : parsed;
        }
      }
    } catch (e) {
      console.error("Failed to load saved options for", key, e);
    }
    return key === STORAGE_KEYS.products ? ensureProductsPresent(defaults) : defaults;
  };

  const saveOptions = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) {
      console.error("Failed to save options for", key, e);
    }
  };

  const [products, setProducts] = React.useState(() =>
    loadOptions(STORAGE_KEYS.products, DEFAULT_PRODUCTS)
  );
  const [persons, setPersons] = React.useState(() =>
    loadOptions(STORAGE_KEYS.persons, DEFAULT_PERSONS)
  );
  const [machines, setMachines] = React.useState(() =>
    loadOptions(STORAGE_KEYS.machines, DEFAULT_MACHINES)
  );
  const [shifts, setShifts] = React.useState(() =>
    loadOptions(STORAGE_KEYS.shifts, DEFAULT_SHIFTS)
  );

  // On mount, pull the current Product/Person/Machine/Shift lists from the
  // shared backend so an ADD or REMOVE done on ANY device is reflected
  // here too - not just the device that made the change. If the request
  // fails (offline, server down, endpoint not built yet), we simply keep
  // whatever loadOptions already put in state (localStorage cache or the
  // hardcoded defaults), so the page still works.
  useEffect(() => {
    let cancelled = false;

    const fetchServerOptions = async () => {
      try {
        const response = await fetch(`${JSONBIN_BASE_URL}/latest`, {
          method: "GET",
          headers: {
            "X-Master-Key": JSONBIN_API_KEY,
            "X-Bin-Meta": "false",
          },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) return;

        // With X-Bin-Meta: false, JSONBin returns the record itself
        // (i.e. { products, persons, machines, shifts }) directly.
        const serverOptions = result || {};

        if (Array.isArray(serverOptions.products) && serverOptions.products.length) {
          const migratedProducts = ensureProductsPresent(
            migrateProductNames(serverOptions.products)
          );
          setProducts(migratedProducts);
          saveOptions(STORAGE_KEYS.products, migratedProducts);
        }
        if (Array.isArray(serverOptions.persons) && serverOptions.persons.length) {
          setPersons(serverOptions.persons);
          saveOptions(STORAGE_KEYS.persons, serverOptions.persons);
        }
        if (Array.isArray(serverOptions.machines) && serverOptions.machines.length) {
          setMachines(serverOptions.machines);
          saveOptions(STORAGE_KEYS.machines, serverOptions.machines);
        }
        if (Array.isArray(serverOptions.shifts) && serverOptions.shifts.length) {
          setShifts(serverOptions.shifts);
          saveOptions(STORAGE_KEYS.shifts, serverOptions.shifts);
        }
      } catch (e) {
        console.error("Failed to fetch shared dropdown options from server:", e);
      }
    };

    fetchServerOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  // Generic ADD handler: gated to Team Leader / HR via the same PIN used
  // for the Download CSV report (see TEAM_LEADER_HR_PIN further below).
  // Once authorized, prompts for a new option, blocks duplicates
  // (case-insensitive), then updates state + localStorage immediately
  // (so this device sees it right away) and pushes the change to the
  // shared backend so every other device picks it up too.
  //
  // `type` is one of the STORAGE_KEYS names ("products"/"persons"/
  // "machines"/"shifts"). Pass { sort: false } for lists like Shift where
  // order matters (1st, 2nd, 3rd, 4th, General) instead of A-Z sorting.
  const handleAddOption = async (
    type,
    list,
    setList,
    labelForPrompt,
    { sort = true } = {}
  ) => {
    const storageKey = STORAGE_KEYS[type];

    const enteredPin = window.prompt(
      `Adding a ${labelForPrompt} is restricted to Team Leader / HR.\nEnter the access PIN to continue:`
    );
    if (enteredPin === null) return; // user hit Cancel

    if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
      alert("Access Denied: Only Team Leader / HR can add options.");
      return;
    }

    const input = window.prompt(`Enter new ${labelForPrompt} to add:`);
    if (input === null) return; // user hit Cancel

    const trimmed = input.trim();
    if (!trimmed) {
      alert("Please enter a non-empty value.");
      return;
    }

    if (list.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
      alert(`"${trimmed}" already exists in the list.`);
      return;
    }

    const updated = sort
      ? [...list, trimmed].sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" })
        )
      : [...list, trimmed];

    setList(updated);
    saveOptions(storageKey, updated);

    await pushOptionsToServer({
      products: type === "products" ? updated : products,
      persons: type === "persons" ? updated : persons,
      machines: type === "machines" ? updated : machines,
      shifts: type === "shifts" ? updated : shifts,
    });

    alert("Added successfully");
  };

  // Generic REMOVE handler: also gated to Team Leader / HR via the PIN.
  // Removes whatever is currently selected in the dropdown, updates this
  // device immediately, and pushes the removal to the shared backend so
  // every other device stops seeing it too. Requires a selection first so
  // the user can't accidentally remove the wrong thing by guessing.
  const handleRemoveOption = async (
    type,
    list,
    setList,
    selectedValue,
    clearSelection,
    labelForPrompt,
    { sort = true } = {}
  ) => {
    const storageKey = STORAGE_KEYS[type];

    const enteredPin = window.prompt(
      `Removing a ${labelForPrompt} is restricted to Team Leader / HR.\nEnter the access PIN to continue:`
    );
    if (enteredPin === null) return; // user hit Cancel

    if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
      alert("Access Denied: Only Team Leader / HR can remove options.");
      return;
    }

    if (!selectedValue) {
      alert(
        `Please select a ${labelForPrompt} from the dropdown first, then click REMOVE.`
      );
      return;
    }

    if (
      !window.confirm(
        `Remove "${selectedValue}" from the ${labelForPrompt} list permanently?`
      )
    ) {
      return;
    }

    const updated = list.filter((o) => o !== selectedValue);
    setList(updated);
    saveOptions(storageKey, updated);
    clearSelection();

    await pushOptionsToServer({
      products: type === "products" ? updated : products,
      persons: type === "persons" ? updated : persons,
      machines: type === "machines" ? updated : machines,
      shifts: type === "shifts" ? updated : shifts,
    });

    alert("Removed successfully");
  };
  // ---------------------------------------------------------------------

 
const [selectedProduct, setSelectedProduct] = React.useState("");
const [selectedPerson, setSelectedPerson] = React.useState("");
const [selectedMachine, setSelectedMachine] = React.useState("");
const [selectedShift, setSelectedShift] = React.useState("");

// Shift options now live in the `shifts` state above (loaded from the
// server/localStorage, defaulting to DEFAULT_SHIFTS) so they can be
// added to or removed from via the buttons under the Shift Name dropdown,
// the same way Product/Person/Machine already work.

// Kept fully separate so a pick made via the Product dropdown only ever
// lands in "Product Operation", and a pick made via the WFH dropdown only
// ever lands in "WFH Operation".
const [productOperation, setProductOperation] = React.useState("");
const [wfhOperation, setWfhOperation] = React.useState("");

// Free-text "Other Operation" field, stored directly into its own column.
const [otherOperation, setOtherOperation] = React.useState("");

const [quantity, setQuantity] = React.useState("");
const [rejection, setRejection] = React.useState("");
const [startTime, setStartTime] = React.useState("");
const [stopTime, setStopTime] = React.useState("");


useEffect(() => {
  const savedProduct = sessionStorage.getItem("selectedProduct");
  if (savedProduct) {
    // Migrate old cached product names (e.g. from a previous session)
    // to their current label so it still matches an entry in the
    // Product dropdown and in hydaxOperations.
    const migratedProduct = PRODUCT_NAME_MIGRATIONS[savedProduct] || savedProduct;
    setSelectedProduct(migratedProduct);
    if (migratedProduct !== savedProduct) {
      sessionStorage.setItem("selectedProduct", migratedProduct);
    }
  }

  const savedWFH = sessionStorage.getItem("selectedWFH");
  if (savedWFH) {
    setSelectedWFH(savedWFH);
  }

  const savedProductOperation = sessionStorage.getItem("selectedProductOperation");
  if (savedProductOperation) {
    setProductOperation(savedProductOperation);
  }

  const savedWFHOperation = sessionStorage.getItem("selectedWFHOperation");
  if (savedWFHOperation) {
    setWfhOperation(savedWFHOperation);
  }
}, []);


  const handleProductChange = (e) => {
  const value = e.target.value;

  setSelectedProduct(value);

  // Save selected product in sessionStorage
  sessionStorage.setItem("selectedProduct", value);

  // A new product means any previously picked Product Operation no longer applies
  sessionStorage.removeItem("selectedProductOperation");
  setProductOperation("");

  if (!value) return;

  const hydaxOperations = {
    "HYDAX 19": [
      "SPARE SLEEVE 19 MOULDING",
      "SPARE SLEEVE 19 ANNEALING SFG",
      "SPARE SLEEVE 19 OD TURNING SFG",
      "SPARE SLEEVE 19 DEBURRING SFG",
      "SPARE SLEEVE 19 TESTING AND MARKING SFG",
      "SPARE HUB 19 SECOND OPERATION SFG",
      "SPARE HUB 19 GEAR CUTTING SFG",
      "SPARE HUB 19 DEBURRING SFG",
      "SPARE HUB 19 BUFFING SFG",
      "SPARE HUB 19 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    "HYDAX 28": [
      "SPARE SLEEVE 28 MOULDING",
      "SPARE SLEEVE 28 ANNEALING SFG",
      "SPARE SLEEVE 28 OD TURNING SFG",
      "SPARE SLEEVE 28 DEBURRING SFG",
      "SPARE SLEEVE 28 TESTING AND MARKING SFG",
      "SPARE HUB 28 SECOND OPERATION SFG",
      "SPARE HUB 28 GEAR CUTTING SFG",
      "SPARE HUB 28 DEBURRING SFG",
      "SPARE HUB 28 BUFFING SFG",
      "SPARE HUB 28 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    "HYDAX 38": [
      "SPARE SLEEVE 38 MOULDING",
      "SPARE SLEEVE 38 ANNEALING SFG",
      "SPARE SLEEVE 38 OD TURNING SFG",
      "SPARE SLEEVE 38 DEBURRING SFG",
      "SPARE SLEEVE 38 TESTING AND MARKING SFG",
      "SPARE HUB 38 SECOND OPERATION SFG",
      "SPARE HUB 38 GEAR CUTTING SFG",
      "SPARE HUB 38 DEBURRING SFG",
      "SPARE HUB 38 BUFFING SFG",
      "SPARE HUB 38 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    "HYDAX 48": [
      "SPARE SLEEVE 48 MOULDING",
      "SPARE SLEEVE 48 ANNEALING SFG",
      "SPARE SLEEVE 48 OD TURNING SFG",
      "SPARE SLEEVE 48 DEBURRING SFG",
      "SPARE SLEEVE 48 TESTING AND MARKING SFG",
      "SPARE HUB 48 SECOND OPERATION SFG",
      "SPARE HUB 48 GEAR CUTTING SFG",
      "SPARE HUB 48 DEBURRING SFG",
      "SPARE HUB 48 BUFFING SFG",
      "SPARE HUB 48 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    "HYDAX 55": [
      "SPARE SLEEVE 55 ANNEALING SFG",
      "SPARE SLEEVE 55 PARTING SFG",
      "SPARE SLEEVE 55 TURNING SFG",
      "SPARE SLEEVE 55 CHAMFERING SFG",
      "SPARE SLEEVE 55 TESTING AND MARKING SFG",
      "SPARE HUB 55 GEAR CUTTING SFG",
      "SPARE HUB 55 DEBURRING SFG",
      "SPARE HUB 55 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    "HYDAX 65": [
      "SPARE SLEEVE 65 ANNEALING SFG",
      "SPARE SLEEVE 65 PARTING SFG",
      "SPARE SLEEVE 65 TURNING SFG",
      "SPARE SLEEVE 65 CHAMFERING SFG",
      "SPARE SLEEVE 65 TESTING AND MARKING SFG",
      "SPARE HUB 65 GEAR CUTTING SFG",
      "SPARE HUB 65 DEBURRING SFG",
      "SPARE HUB 65 TESTING AND MARKING SFG",
      "Marking and Packing",
    ],

    // Selecting "BT 250 UM 40 1/2\"BSP" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    'BT 250 UM 40 1/2"BSP': [
      'BT 250 1/2" HEX BAR CUTTING',
      'BT 250 1/2" FIRST OPERATION',
      'BT 250 1/2" THREADING OPERATION',
      'BT 250 1/2" FACING',
      'BT 250 1/2" EMERY',
    ],

    // Selecting "BT 700 TT 40 1\"BSP" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    'BT 700 TT 40 1"BSP': [
      'BT 700 1" HEX BAR CUTTING',
      'BT 700 1" FIRST OPERATION',
      'BT 700 1" THREADING OPERATION',
      'BT 700 1" FACING',
      'BT 700 1" EMERY',
    ],

    // Selecting "BT-250 TT 40 M12" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "BT-250 TT 40 M12": [
      'BT 250 1/2" HEX BAR CUTTING',
      'BT 250 1/2" FIRST OPERATION',
      'BT 250 1/2" THREADING OPERATION',
      'BT 250 1/2" FACING',
      'BT 250 1/2" EMERY',
    ],

    // Selecting "FB 250 SS FLANGE" from the Product dropdown will now
    // show these 3 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "FB 250 SS FLANGE": [
      "FB250 SS COLLAR CUTTING",
      "FB250 SS FLANGE COLLAR WELDING",
      "FB250 SS STRAINER ASSEMBLY",
    ],

    // Selecting "FB 250 TT 40 (SS STRAINER)" from the Product dropdown will
    // now show these 2 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    "FB 250 TT 40 (SS STRAINER)": [
      "FB250 SS COLLAR CUTTING",
      "FB250 SS FLANGE COLLAR WELDING",
    ],

    // Selecting "BT-700 UM 40 3/4\"BSP" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    'BT-700 UM 40 3/4"BSP': [
      'BT 700 3/4" HEX BAR CUTTING',
      'BT 700 3/4" FIRST OPERATION',
      'BT 700 3/4" THREADING OPERATION',
      'BT 700 3/4" FACING',
      'BT 700 3/4" EMERY',
    ],

    // Selecting "BT 700 TT 40 M30" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "BT 700 TT 40 M30": [
      "BT 700 M30 HEX BAR CUTTING",
      "BT 700 M30 FIRST OPERATION",
      "BT 700 M30 THREADING OPERATION",
      "BT 700 M30 FACING",
      "BT 700 M30 EMERY",
    ],

    // Selecting "BT-700 UM 40 1\"" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    'BT-700 UM 40 1"': [
      'BT 700 1/2" HEX BAR CUTTING',
      'BT 700 1/2" FIRST OPERATION',
      'BT 700 1/2" THREADING OPERATION',
      'BT 700 1/2" FACING',
      'BT 700 1/2" EMERY',
    ],

    // Selecting "BT-700 UM 40 1/2\"" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    'BT-700 UM 40 1/2"': [
      'BT 700 1/2" HEX BAR CUTTING',
      'BT 700 1/2" FIRST OPERATION',
      'BT 700 1/2" THREADING OPERATION',
      'BT 700 1/2" FACING',
      'BT 700 1/2" EMERY',
    ],

    // Selecting "BT 700 UM 40 M 34" from the Product dropdown will now
    // show these 5 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "BT 700 UM 40 M 34": [
      "BT 700 M34 HEX BAR CUTTING",
      "BT 700 M34 FIRST OPERATION",
      "BT 700 M34 THREADING OPERATION",
      "BT 700 M34 FACING",
      "BT 700 M34 EMERY",
    ],

    // Selecting "BT-250 TT 40 M10 ASSEMBLY" from the Product dropdown will
    // now show these 5 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    "BT-250 TT 40 M10 ASSEMBLY": [
      'BT 250 1/2" HEX BAR CUTTING',
      'BT 250 1/2" FIRST OPERATION',
      'BT 250 1/2" THREADING OPERATION',
      'BT 250 1/2" FACING',
      'BT 250 1/2" EMERY',
    ],

    // Selecting "SS6063 1\" BSP-P" from the Product dropdown will now show
    // "SST" as a clickable operation on the Operations page, instead of
    // falling back to just showing the product name itself.
  'SS6063 1" BSP-P': [
  '1" NUT CAP THREADING',
  "SS60L MESH PLEATING",
  "SS60L MESH CUTTING",
  "SS60L MESH JOINING",
],

'SS3401402 2 1/2" BSP-M': [
  '2 1/2" NUT CAP THREADING',
  '2 1/2" NUT CAP BORING/FACING',
  'SS 340L BOTTOM CAP FACING',
  'SS 340L BOTTOM CAP 1MM DRILLING',
  'SS 340L BOTTOM CAP ZINC PLATING',
],

'SS4501403 BSP-M': [
  "SS 450L NUT CAP BORING/FACING",
  "SS 450L NUT CAP THREADING",
  "SS 450L MESH CUTTING",
  "SS 450L MESH JOINING",
],

'SS15633/4"BSP-P': [
  "SS15L SPL MESH PLEATING",
  "SS15L SPL MESH CUTTING",
  "SS15L SPL MESH JOINING",
],

'SS2501402 BSP-M': [
  "SS 250L SPL NUT CAP BORING/FACING",
  "SS 250L SPL NUT CAP THREADING",
  "SS 250L SPL BOTTOM CAP FACING",
  "SS 250L SPL BOTTOM CAP 1MM DRILLING",
  "SS250L SPL MESH PLEATING",
  "SS250L SPL BOTTOM CAP ZINC PLATING",
  "SS250L SPL MESH CUTTING",
  "SS250L SPL MESH JOINING",
],


'SS200902 BSP-P': [
  '2" NUT CAP THREADING',
  "SS200L MESH PLEATING",
  "SS200L MESH CUTTING",
  "SS200L MESH JOINING",
],

'SS125901  1/2" BSP-P': [
  '1 1/2" NUT CAP THREADING',
  "SS125L MESH PLEATING",
  "SS125L MESH CUTTING",
  "SS125L MESH JOINING",
],

'SS3001402  1/2" BSP-M': [
  "SS300L MESH PLEATING",
  '2 1/2" NUT CAP BORING/FACING',
  '2 1/2" NUT CAP THREADING',
  "SS 300L BOTTOM CAP FACING",
  "SS 300L BOTTOM CAP 1MM DRILLING",
  "SS 300L BOTTOM CAP ZINC PLATING",
  "SS300L MESH CUTTING",
  "SS300L MESH JOINING",
],

'SS6041/ 90 LPM 1" BSPT': [
  '1" NUT CAP THREADING',
  "SS6041L MESH PLEATING",
  "SS6041L MESH CUTTING",
  "SS6041L MESH JOINING",
],

'SS250902" BSP-P': [
  '2" NUT CAP THREADING',
  "SS250L MESH PLEATING",
  "SS250L MESH CUTTING",
  "SS250L MESH JOINING",
],

'SS30633/4" BSP-P': [
  '3/4" NUT CAP THREDING',
  "SS30L MESH PLEATING",
  "SS30L MESH CUTTING",
  "SS30L MESH JOINING",
],

'SS1552 1/2" BSP-P': [
  '1/2" NUT CAP THREADING',
  "SS15L MESH PLEATING",
  "SS15L MESH CUTTING",
  "SS15L MESH JOINING",
],

'SS90901 1/2" BSP-P': [
  '1 1/2" NUT CAP THREADING',
  "SS90L MESH PLEATING",
  "SS90L MESH CUTTING",
  "SS90L MESH JOINING",
],

'SS160901 1/2" BSP-P': [
  '1 1/2" NUT CAP THREADING',
  "SS160L MESH PLEATING",
  "SS160L MESH CUTTING",
  "SS160L MESH JOINING",
],

    // Selecting "RLF-04-10-TT" from the Product dropdown will now show
    // these 6 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-04-10-TT": [
      "RLF 04 BODY SHOT BLASTING",
      "RLF 04 BODY FACING 1",
      "RLF 04 BODY FACING 2",
      "RLF 04 BODY TAPPING",
      "RLF 04 BOX FACING",
      "RLF 04 BODY TESTING",
    ],

    // Selecting "RLF-04-10-TT-VI (1/8\" NPT)" from the Product dropdown will
    // now show these 6 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    'RLF-04-10-TT-VI (1/8" NPT)': [
      "RLF 04 BODY SHOT BLASTING",
      "RLF 04 BODY FACING 1",
      "RLF 04 BODY FACING 2",
      "RLF 04 BODY TAPPING",
      "RLF 04 BOX FACING",
      "RLF 04 BODY TESTING",
    ],

    // Selecting "RLF-06-25-TT" from the Product dropdown will now show
    // these 6 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-06-25-TT": [
      "RLF 06 BODY SHOT BLASTING",
      "RLF 06 BODY FACING 1",
      "RLF 06 BODY FACING 2",
      "RLF 06 BODY TAPPING",
      "RLF 06 BOX FACING",
      "RLF 06 BODY TESTING",
    ],

    // Selecting "RLF-08-10-TT" from the Product dropdown will now show
    // these 11 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-08-10-TT": [
      "RLF 08 BODY SHOT BLASTING",
      "RLF 08 BODY FACING 1",
      "RLF 08 BODY FACING 2",
      "RLF 08 BODY TAPPING",
      "RLF 08 BODY TESTING",
      "RLF 08 BOWL MOULDING",
      "RLF 08 BOWL TURNING",
      "RLF 08 BOWL BORING",
      "RLF 06/08 COVER SHOT BLASTING",
      "RLF 06/08 COVER FACING",
      "RLF 06/08 COVER TESTING",
    ],

    // Selecting "RLF-08-25-TT" from the Product dropdown will now show
    // these 10 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-08-25-TT": [
      "RLF 08 BODY SHOT BLASTING",
      "RLF 08 BODY FACING 1",
      "RLF 08 BODY FACING 2",
      "RLF 08 BODY TAPPING",
      "RLF 08 BODY TESTING",
      "RLF 08 BOWL TURNING",
      "RLF 08 BOWL BORING",
      "RLF 06/08 COVER SHOT BLASTING",
      "RLF 06/08 COVER FACING",
      "RLF 06/08 COVER TESTING",
    ],

    // Selecting "RLF-12-25-TT" from the Product dropdown will now show
    // these 10 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-12-25-TT": [
      "RLF 12 BODY SHOT BLASTING",
      "RLF 12 BODY FACING 1",
      "RLF 12  BODY FACING 2",
      "RLF 12 BODY TAPPING",
      "RLF 12 BODY TESTING",
      "RLF 12  BOWL TURNING",
      "RLF 12 BOWL BORING",
      "RLF 10/12 COVER SHOT BLASTING",
      "RLF 10/12 COVER FACING",
      "RLF 10/12 COVER TESTING",
    ],

    // Selecting "RLF-10-25-TT" from the Product dropdown will now show
    // these 10 operations as clickable items on the Operations page,
    // instead of falling back to just showing the product name itself.
    "RLF-10-25-TT": [
      "RLF 10 BODY SHOT BLASTING",
      "RLF 10 BODY FACING 1",
      "RLF 10 BODY FACING 2",
      "RLF 10 BODY TAPPING",
      "RLF 10 BODY TESTING",
      "RLF 02 BOWL TURNING",
      "RLF 10 BOWL BORING",
      "RLF 10/12 COVER SHOT BLASTING",
      "RLF 10/12 COVER FACING",
      "RLF 10/12 COVER TESTING",
    ],

    // Selecting "RLF-04-25-TT-VI (1/8\" NPT)" from the Product dropdown will
    // now show these 11 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    'RLF-04-25-TT-VI (1/8" NPT)': [
      "RLF 04 BODY SHOT BLASTING",
      "RLF 04 BODY FACING 1",
      "RLF 04 BODY FACING 2",
      "RLF 04 BODY TAPPING",
      "RLF 04 BODY TESTING",
      "RLF 04 BOWL TURNING",
      "RLF 04 BOWL BORING",
      "RLF 04  COVER SHOT BLASTING",
      "RLF 04 VI COVER FACING",
      "RLF 04 VI COVER DRILLING AND TAPPING",
      "RLF 04 VI COVER TESTING",
    ],

    // Selecting "RLF-04-25-TT-VI (1/8\" BSPT) ( W/O VI)" from the Product
    // dropdown will now show these 11 operations as clickable items on the
    // Operations page, instead of falling back to just showing the product
    // name itself.
    'RLF-04-25-TT-VI (1/8" BSPT) ( W/O VI)': [
      "RLF 04 BODY SHOT BLASTING",
      "RLF 04 BODY FACING 1",
      "RLF 04 BODY FACING 2",
      "RLF 04 BODY TAPPING",
      "RLF 04 BODY TESTING",
      "RLF 04 BOWL TURNING",
      "RLF 04 BOWL BORING",
      "RLF 04 COVER SHOT BLASTING",
      "RLF 04 VI COVER FACING",
      "RLF 04 VI COVER DRILLING AND TAPPING",
      "RLF 04 VI COVER TESTING",
    ],

    // Selecting "RLF-06-25-TT-VI (1/8\" NPT)" from the Product dropdown will
    // now show these 11 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    'RLF-06-25-TT-VI (1/8" NPT)': [
      "RLF 06 BODY SHOT BLASTING",
      "RLF 06 BODY FACING 1",
      "RLF 06 BODY FACING 2",
      "RLF 06 BODY TAPPING",
      "RLF 06 BODY TESTING",
      "RLF 06 BOWL TURNING",
      "RLF 06 BOWL BORING",
      "RLF 06/08 COVER SHOT BLASTING",
      "RLF 06/08  COVER FACING",
      "RLF 06/08 VI COVER DRILLING AND TAPPING",
      "RLF 06/08 VI COVER TESTING",
    ],

    // Selecting "RLF-08-10-TT-VI (1/8\" NPT)" from the Product dropdown will
    // now show these 11 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    'RLF-08-10-TT-VI (1/8" NPT)': [
      "RLF 08 BODY SHOT BLASTING",
      "RLF 08 BODY FACING 1",
      "RLF 08 BODY FACING 2",
      "RLF 08 BODY TAPPING",
      "RLF 08 BODY TESTING",
      "RLF 08 BOWL TURNING",
      "RLF 08 BOWL BORING",
      "RLF 06/08 COVER SHOT BLASTING",
      "RLF 06/08 COVER FACING",
      "RLF 06/08 VI COVER DRILLING AND TAPPING",
      "RLF 06/08 VI COVER TESTING",
    ],

    // Selecting "RLF-10-25-TT-VI (1/8\" NPT)" from the Product dropdown will
    // now show these 11 operations as clickable items on the Operations
    // page, instead of falling back to just showing the product name itself.
    'RLF-10-25-TT-VI (1/8" NPT)': [
      "RLF 10 BODY SHOT BLASTING",
      "RLF 10 BODY FACING 1",
      "RLF 10 BODY FACING 2",
      "RLF 10 BODY TAPPING",
      "RLF 10 BODY TESTING",
      "RLF 10 BOWL TURNING",
      "RLF 10 BOWL BORING",
      "RLF 10/12 COVER SHOT BLASTING",
      "RLF 10/12 COVER FACING",
      "RLF 10/12 VI COVER DRILLING AND TAPPING",
      "RLF 10/12 VI COVER TESTING",
    ],

    // Selecting "SGR-03" from the Product dropdown will now show these 2
    // operations as clickable items on the Operations page, instead of
    // falling back to just showing the product name itself.
    "SGR-03": [
      "M12 BOLT WITH HOLES",
      "M12 BOLT ZINC PLATING",
    ],

    // Selecting "SGR-05" from the Product dropdown will now show these 2
    // operations as clickable items on the Operations page, instead of
    // falling back to just showing the product name itself.
    "SGR-05": [
      "M12 BOLT WITH HOLES",
      "M12 BOLT ZINC PLATING",
    ],

    // Selecting "SGR-10" from the Product dropdown will now show these 2
    // operations as clickable items on the Operations page, instead of
    // falling back to just showing the product name itself.
    "SGR-10": [
      "M16 BOLT WITH HOLES",
      "M16 BOLT ZINC PLATING",
    ],

    // Selecting "SGR-14" from the Product dropdown will now show these 2
    // operations as clickable items on the Operations page, instead of
    // falling back to just showing the product name itself.
    "SGR-14": [
      "M20 BOLT WITH HOLES",
      "M20 BOLT ZINC PLATING",
    ],

  };

  if (hydaxOperations[value]) {
    navigate("/operations", {
      state: {
        productName: value,
        description: `${value} Operations`,
        operations: hydaxOperations[value],
        operationType: "product",
      },
    });
    return;
  }

  navigate("/operations", {
    state: {
      productName: value,
      description: `${value} Operations`,
      operations: [value],
      operationType: "product",
    },
  });
};

  const [selectedWFH, setSelectedWFH] = React.useState("");

const handleWFHChange = (e) => {
  const value = e.target.value;

  setSelectedWFH(value);

  // Save selected value in sessionStorage
  sessionStorage.setItem("selectedWFH", value);

  // A new WFH selection means any previously picked WFH Operation no longer applies
  sessionStorage.removeItem("selectedWFHOperation");
  setWfhOperation("");

  // WFH and Machine are mutually exclusive
  if (value) {
    setSelectedMachine("");
  }

  if (value === "Harsh") {
    navigate("/operations", {
      state: {
        productName: "Harsh",
        description: "WFH Activities",
        operations: [
          "1. Procurement",
          "2. Design",
          "3. Production Entries",
          "4. Inventory Management",
        ],
        operationType: "wfh",
      },
    });
  }
}; 
  

    

 

 const handlePersonChange = (e) => {
  const person = e.target.value;

  if (!person) return;

  console.log("Selected Person:", person);
};

const handleMachineChange = (e) => {
  const machine = e.target.value;

  if (!machine) return;

  // Machine and WFH are mutually exclusive
  setSelectedWFH("");
  sessionStorage.removeItem("selectedWFH");

  console.log("Selected Machine:", machine);
};


const inputStyle = {
  width: "100%",
  height: "clamp(48px, 6vw, 90px)",
  padding: "0 clamp(12px, 2vw, 20px)",
  borderRadius: "14px",
  border: "2px solid #d1d5db",
  fontSize: "clamp(15px, 2.2vw, 22px)",
  fontWeight: "700",
  background: "#ffffff",
  boxSizing: "border-box",
  cursor: "pointer",
  maxWidth: "100%",
};

const labelStyle = {
  display: "block",
  height: "auto",
  marginBottom: "12px",
  fontWeight: "700",
  fontSize: "clamp(13px, 1.6vw, 15px)",
};

const buttonStyle = {
  width: "100%",
  height: "clamp(48px, 6vw, 90px)",
  border: "none",
  borderRadius: "14px",
  fontSize: "clamp(14px, 1.8vw, 18px)",
  fontWeight: "700",
  cursor: "pointer",
  background: "#22c55e",
  color: "#fff",
};

// Small ADD/REMOVE pair shown under a dropdown. Kept compact (side by
// side, roughly half-width each) so it doesn't compete visually with the
// dropdown itself.
const optionButtonRowStyle = {
  display: "flex",
  gap: "10px",
  marginTop: "10px",
};

const addOptionButtonStyle = {
  flex: 1,
  height: "clamp(38px, 5vw, 48px)",
  border: "none",
  borderRadius: "10px",
  fontSize: "clamp(12px, 1.5vw, 15px)",
  fontWeight: "700",
  cursor: "pointer",
  background: "#22c55e",
  color: "#fff",
  transition: "background 0.2s ease",
};

const removeOptionButtonStyle = {
  flex: 1,
  height: "clamp(38px, 5vw, 48px)",
  border: "none",
  borderRadius: "10px",
  fontSize: "clamp(12px, 1.5vw, 15px)",
  fontWeight: "700",
  cursor: "pointer",
  background: "#ef4444",
  color: "#fff",
  transition: "background 0.2s ease",
};


const handleSubmit = async (event) => {
  event.preventDefault();

  if (
    !selectedPerson ||
    (!selectedMachine && !selectedWFH) ||
    !selectedShift ||
    quantity === "" ||
    rejection === "" ||
    !startTime ||
    !stopTime
  ) {
    alert("Please fill all fields.");
    return;
  }

  const payload = {
    person: selectedPerson,
    product: selectedProduct,
    wfh: selectedWFH,
    machine: selectedMachine,
    shiftName: selectedShift,
    // Each column now stores only what was picked from its own dropdown -
    // Product Operation comes only from the Select Product flow, WFH
    // Operation comes only from the Select WFH flow, and Other Operation
    // comes only from the manual text field below.
    productOperation: productOperation,
    wfhOperation: wfhOperation,
    otherOperation: otherOperation,
    quantity: quantity,
    rejection: rejection,
    startTime: startTime,
    stopTime: stopTime,
  };

  try {
    const response = await fetch("/BDWRTAPI", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (response.ok) {
      alert("Recorded Successfully");

      setSelectedPerson("");
      setSelectedProduct("");
      setSelectedWFH("");
      setSelectedMachine("");
      setSelectedShift("");
      setProductOperation("");
      setWfhOperation("");
      setOtherOperation("");
      setQuantity("");
      setRejection("");
      setStartTime("");
      setStopTime("");

      sessionStorage.removeItem("selectedProduct");
      sessionStorage.removeItem("selectedWFH");
      sessionStorage.removeItem("selectedProductOperation");
      sessionStorage.removeItem("selectedWFHOperation");
    } else {
      alert(result.error || "Failed to save data.");
    }
  } catch (error) {
    console.error(error);
    alert("Server Error.");
  }
};



// NOTE: Recording an entry is now handled by handleSubmit (above), which
// POSTs to the shared backend at /BDWRTAPI. That is intentional - since
// every employee uses their own device, saving to localStorage would only
// ever be visible on that one device. Routing every submission through the
// backend is what lets Team Leader / HR later download everyone's entries
// from a single place, regardless of which device each entry came from.
// The Submit button below calls handleSubmit directly.



// Exports every entry every employee has submitted, by fetching them from
// the shared backend (not from this device's local storage - local storage
// can't see entries submitted from other employees' devices).
//
// ASSUMPTION: this expects a GET request to the same /BDWRTAPI endpoint
// used for submitting entries to return a JSON array of all stored entries,
// e.g. [{ person, product, wfh, machine, productOperation, wfhOperation,
// otherOperation, quantity, rejection, startTime, stopTime, ... }, ...].
// If your backend exposes a different route or response shape for reading
// records back, update the URL and the "records" line below to match.
// Fetches every entry already stored in MySQL (across ALL employees and
// devices) and exports it as CSV. Team Leader/HR do not need to fill or
// submit anything themselves - this only reads what's already been saved.
// --- Date filtering helpers ---
// Pulls the date portion out of a created_at value and always returns it as
// a canonical "YYYY-MM-DD" key, no matter which of these formats it actually
// arrives in:
//   - "YYYY-MM-DD HH:mm:ss"      (typical MySQL DATETIME)
//   - "YYYY-MM-DDTHH:mm:ss.000Z" (full ISO string)
//   - "DD-MM-YYYY HH:mm"         (e.g. "13-08-2026 11:54", what this app's
//                                  own data has been observed to store)
//   - "DD/MM/YYYY HH:mm"
// Naively slicing the first 10 characters (the old approach) only works for
// the YYYY-MM-DD case - for "DD-MM-YYYY" it grabs the day/month/year in the
// wrong order, which silently breaks both the single-date filter (never
// matches, or matches the wrong day) and the range filter (comparing
// differently-ordered strings with >= / <= is meaningless and can match
// dates that have nothing to do with the range you asked for). Parsing the
// value into real year/month/day parts first avoids all of that, and works
// identically for the 1st through the 31st.
const getDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return null;
  const str = String(dateTimeStr).trim();

  // "YYYY-MM-DD..." (MySQL DATETIME / ISO) - year comes first.
  let m = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const [, y, mo, d] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // "DD-MM-YYYY..." - year comes last, day/month first (what the app's own
  // exports have shown, e.g. "13-08-2026 11:54").
  m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // "DD/MM/YYYY..." - same as above but slash-separated.
  m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // Fallback: let the JS Date parser take a shot at anything else.
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  }

  return null;
};

// --- Field-name resilience -------------------------------------------------
// The backend has been observed to send column names in more than one
// casing/spelling (e.g. "created_at" vs "createdAt", "shift_name" vs
// "shiftName"). Rather than hard-code a single exact key and silently break
// (row.created_at === undefined -> every date filter matches nothing, e.g.
// "No records found for the selected date filter" even though the DB has
// rows for that day), every canonical column below is looked up under all
// of its known aliases and the first defined/non-null one wins.
const FIELD_ALIASES = {
  person: ["person"],
  shift_name: ["shift_name", "shiftName", "shift"],
  product: ["product"],
  product_operation: ["product_operation", "productOperation"],
  machine: ["machine"],
  other_operation: ["other_operation", "otherOperation"],
  wfh: ["wfh"],
  wfh_operation: ["wfh_operation", "wfhOperation"],
  startTime: ["startTime", "start_time"],
  stopTime: ["stopTime", "stop_time"],
  quantity: ["quantity"],
  rejection: ["rejection"],
  created_at: ["created_at", "createdAt", "created_On", "createdOn", "timestamp", "date"],
};

const getFieldValue = (row, canonicalKey) => {
  const aliases = FIELD_ALIASES[canonicalKey] || [canonicalKey];
  for (const alias of aliases) {
    if (row[alias] !== undefined && row[alias] !== null && row[alias] !== "") {
      return row[alias];
    }
  }
  return "";
};

// Defends against a backend response shape that has been observed where a
// row's values echo their own column names (e.g. { person: "person",
// shift_name: "shift_name", ... }) instead of real data - see the CSV
// screenshot where every data row was identical to the header row. Such a
// row is corrupt/placeholder data, not a real entry, so it's dropped from
// exports rather than silently shown as if it were a genuine record.
const isCorruptEchoRow = (row) => {
  const keysToCheck = ["person", "shift_name", "product", "machine"];
  return keysToCheck.every((key) => {
    const val = getFieldValue(row, key);
    return val !== "" && String(val).trim().toLowerCase() === key.toLowerCase();
  });
};

// filterMode: "all" | "single" | "range"
// - "single": keeps rows whose created_at date === dateFrom (e.g. only Aug 11th)
// - "range": keeps rows whose created_at date is between dateFrom and dateTo,
//   inclusive (e.g. Aug 10th through Aug 12th) - this is "condition2".
// Dates are expected as "YYYY-MM-DD" strings, which sort/compare correctly
// as plain strings since they're zero-padded and in year-month-day order.
const filterDataByDate = (data, filterMode, dateFrom, dateTo) => {
  if (filterMode === "single" && dateFrom) {
    return data.filter(
      (row) => getDateOnly(getFieldValue(row, "created_at")) === dateFrom
    );
  }

  if (filterMode === "range" && dateFrom && dateTo) {
    return data.filter((row) => {
      const rowDate = getDateOnly(getFieldValue(row, "created_at"));
      if (!rowDate) return false;
      return rowDate >= dateFrom && rowDate <= dateTo;
    });
  }

  // "all" (or anything unrecognized) - no filtering
  return data;
};

// --- CSV date formatting ----------------------------------------------------
// Reformats a created_at value for the CSV export only, as "DD-MM-YYYY"
// (date, month, year) - e.g. "2026-08-13 11:54:00" -> "13-08-2026 11:54:00".
// Reuses getDateOnly (which already understands every raw format the backend
// has been observed to send) to get a canonical "YYYY-MM-DD", then just
// re-orders the parts. If the value can't be parsed at all, it's left
// untouched rather than dropped, so nothing is silently lost in the export.
// NOTE: this is only for what gets written into the CSV cell - the filtering
// logic above (getDateOnly / filterDataByDate) keeps comparing against the
// canonical "YYYY-MM-DD" form, since that's what sorts/compares correctly.
const formatDateForCSV = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const str = String(dateTimeStr).trim();

  const isoDate = getDateOnly(str); // "YYYY-MM-DD" or null
  if (!isoDate) return str;

  const [y, mo, d] = isoDate.split("-");
  const formattedDate = `${d}-${mo}-${y}`;

  // Preserve a time component if the original value had one (e.g. "11:54"
  // or "11:54:00") so exports don't lose it - just place it after the
  // reordered date.
  const timeMatch = str.match(/(\d{1,2}:\d{2}(:\d{2})?)/);
  return timeMatch ? `${formattedDate} ${timeMatch[1]}` : formattedDate;
};

// filterMode/dateFrom/dateTo let the caller (handleDownloadClick) ask for:
//   - every record ("all")
//   - one specific date only, e.g. 2026-08-11 ("single")
//   - a date range, e.g. 2026-08-10 to 2026-08-12 ("range" / condition2)
const downloadCSV = async (filterMode = "all", dateFrom = null, dateTo = null) => {
  try {
    const response = await fetch("/BDGETALLAPI", {
      method: "GET",
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      alert(result.error || "Failed to fetch data from server.");
      return;
    }

    let allData = result.data || [];

    // Debug aid: log the raw shape of one record so the actual key names
    // the backend is sending can be inspected in the browser console if
    // filtering/export still looks wrong (e.g. open DevTools > Console
    // right after clicking Download CSV).
    if (allData.length > 0) {
      console.log("[Hydraulics] Sample record from /BDGETALLAPI:", allData[0]);
    }

    if (allData.length === 0) {
      alert("No data recorded yet in the database.");
      return;
    }

    // Drop corrupt/placeholder rows where the values just echo the column
    // names back (see isCorruptEchoRow) - these are not real entries and
    // should never end up in a Team Leader/HR export.
    const corruptCount = allData.filter(isCorruptEchoRow).length;
    if (corruptCount > 0) {
      console.warn(
        `[Hydraulics] Dropped ${corruptCount} corrupt record(s) whose values matched their own column names.`
      );
    }
    allData = allData.filter((row) => !isCorruptEchoRow(row));

    if (allData.length === 0) {
      alert(
        "No usable data recorded yet in the database (all records looked corrupt)."
      );
      return;
    }

    // Apply the date filter chosen in handleDownloadClick before exporting.
    allData = filterDataByDate(allData, filterMode, dateFrom, dateTo);

    if (allData.length === 0) {
      alert("No records found for the selected date filter.");
      return;
    }

    // Fixed column order
    const headers = [
      "person",
      "shift_name",
      "product",
      "product_operation",
      "machine",
      "other_operation",
      "wfh",
      "wfh_operation",
      "startTime",
      "stopTime",
      "quantity",
      "rejection",
      "created_at",
    ];

    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(","));

    // Add data rows in the exact same order. getFieldValue looks each
    // column up under every known alias (see FIELD_ALIASES) instead of a
    // single hard-coded key, so a backend that sends "createdAt" instead of
    // "created_at" (etc.) still populates correctly instead of coming back
    // blank/undefined.
    allData.forEach((row) => {
      const values = headers.map((header) => {
        let value = getFieldValue(row, header);

        // Store the created_at date as date,month,year (DD-MM-YYYY) in the
        // CSV, regardless of whatever order the backend sent it in.
        if (header === "created_at") {
          value = formatDateForCSV(value);
        }

        return `"${value !== undefined && value !== null ? value : ""}"`;
      });

      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    // Name the file after whichever filter was applied so Team Leader/HR
    // can tell at a glance which records it contains.
    let filename = "Hydraulics_Data.csv";
    if (filterMode === "single" && dateFrom) {
      filename = `Hydraulics_Data_${dateFrom}.csv`;
    } else if (filterMode === "range" && dateFrom && dateTo) {
      filename = `Hydraulics_Data_${dateFrom}_to_${dateTo}.csv`;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    alert("Server Error while fetching data for download.");
  }
};

// Accepts a date typed as "YYYY-M-D", "YYYY-MM-D", "YYYY-M-DD", or
// "YYYY-MM-DD" (i.e. the day/month can be 1 or 2 digits - so "2026-8-1",
// "2026-08-1", "2026-8-01" and "2026-08-01" are all treated the same way)
// and returns it zero-padded as "YYYY-MM-DD", which is what filterDataByDate
// / getDateOnly compare against. Also rejects dates that don't actually
// exist on the calendar (e.g. 2026-02-30). Returns null if the input can't
// be parsed into a real date at all.
const normalizeDateInput = (raw) => {
  if (!raw) return null;

  const match = String(raw)
    .trim()
    .match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Confirm it's a real calendar date (catches things like Feb 30, Apr 31).
  const check = new Date(year, month - 1, day);
  if (
    check.getFullYear() !== year ||
    check.getMonth() !== month - 1 ||
    check.getDate() !== day
  ) {
    return null;
  }

  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
};

// --- Access control: Download CSV is restricted to Team Leader / HR ---
// Only people who know this PIN can download the report of every
// employee's entries. Change this PIN whenever your Team Leader / HR agree
// on a new one - it's the only thing gating access, so keep it private and
// don't share it with other employees. Submitting entries (handleSubmit,
// above) stays open to everyone; only the export/download step is gated.
const TEAM_LEADER_HR_PIN = "2580";

const handleDownloadClick = () => {
  const enteredPin = window.prompt(
    "This report is restricted to Team Leader / HR.\nEnter the access PIN to continue:"
  );

  // User closed/cancelled the prompt - do nothing
  if (enteredPin === null) return;

  if (enteredPin.trim() !== TEAM_LEADER_HR_PIN) {
    alert("Access Denied: Only Team Leader / HR can download this report.");
    return;
  }

  // Ask what to export: every record, one specific date (e.g. Aug 11th
  // only), or a date range (condition2, e.g. Aug 10th to Aug 12th).
  const filterChoice = window.prompt(
    "Which records do you want to download?\n\n" +
      "1 = All Records\n" +
      "2 = A Single Date (e.g. 2026-08-11)\n" +
      "3 = A Date Range (e.g. 2026-08-10 to 2026-08-12)\n\n" +
      "Enter 1, 2, or 3:"
  );

  // User closed/cancelled the prompt - do nothing
  if (filterChoice === null) return;

  const choice = filterChoice.trim();

  if (choice === "1") {
    // Explicitly handle "1" -> every record, for every date, in the DB.
    downloadCSV("all", null, null);
    return;
  }

  if (choice === "2") {
    const singleDate = window.prompt(
      "Enter the date to fetch records for, in YYYY-MM-DD format " +
        "(e.g. 2026-08-01, 2026-08-2, 2026-08-11, 2026-08-31 - any day 1 to 31 works):"
    );
    if (!singleDate) return;

    // Accepts 1st, 2nd, 3rd ... 31st - with or without a leading zero.
    const normalizedDate = normalizeDateInput(singleDate);
    if (!normalizedDate) {
      alert(
        "Invalid date. Please use YYYY-MM-DD (e.g. 2026-08-1 or 2026-08-01) with a real calendar date."
      );
      return;
    }

    downloadCSV("single", normalizedDate, null);
  } else if (choice === "3") {
    const fromDate = window.prompt(
      "Enter the START date, in YYYY-MM-DD format (e.g. 2026-08-10, any day 1 to 31 works):"
    );
    if (!fromDate) return;

    const toDate = window.prompt(
      "Enter the END date, in YYYY-MM-DD format (e.g. 2026-08-12, any day 1 to 31 works):"
    );
    if (!toDate) return;

    // Accepts 1st, 2nd, 3rd ... 31st - with or without a leading zero.
    const normalizedFrom = normalizeDateInput(fromDate);
    const normalizedTo = normalizeDateInput(toDate);

    if (!normalizedFrom || !normalizedTo) {
      alert(
        "Invalid date. Please use YYYY-MM-DD (e.g. 2026-08-1 or 2026-08-01) with real calendar dates for both fields."
      );
      return;
    }

    if (normalizedFrom > normalizedTo) {
      alert("Start date must be on or before the end date.");
      return;
    }

    downloadCSV("range", normalizedFrom, normalizedTo);
  } else {
    // Empty input or anything unrecognized -> default to exporting
    // every record for every date (same as explicitly entering "1").
    downloadCSV("all", null, null);
  }
};



// Confirms the typed "Other Operation" text and keeps it sitting in the
// input field. It is NOT written to the CSV at this point - it is only
// held in the otherOperation state. The actual save happens later, in
// handleSubmit, whenever the "Submit" button is clicked (same as every
// other field on this page).
const handleAddOtherOperation = () => {
  const value = otherOperation.trim();

  if (!value) {
    alert("Please enter an Other Operation before adding.");
    return;
  }

  // Normalize the field (trims stray spaces) while leaving the text in
  // place in the input for the user to see/edit further if needed.
  setOtherOperation(value);

  alert(
    `"${value}" added. It will be stored in the Other Operation column when you click Submit.`
  );
};











  return (
  <div
    style={{
      minHeight: "100vh",
      padding: "clamp(14px, 4vw, 30px)",
      background:
        "linear-gradient(135deg,#03045e,#240046,#5a189a,#7209b7)",
      color: "#fff",
      fontFamily: "'Segoe UI', Arial, sans-serif",
      boxSizing: "border-box",
    }}
  >
    {/* Responsive breakpoints for the field grid so Select Product, WFH
        and every other field stay full-size and readable on tablets and
        phones instead of squeezing into 4 tiny columns. */}
    <style>{`
      .hydraulics-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: clamp(16px, 3vw, 40px);
        margin-top: 20px;
        align-items: start;
        width: 100%;
      }
      @media (max-width: 1024px) {
        .hydraulics-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
      @media (max-width: 600px) {
        .hydraulics-grid {
          grid-template-columns: 1fr;
        }
      }
      .hydraulics-select,
      .hydraulics-select option {
        font-size: clamp(15px, 2.2vw, 22px);
      }
    `}</style>

    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "clamp(18px, 4vw, 40px)",
        borderRadius: "20px",
        background: "rgba(255,255,255,0.10)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        boxSizing: "border-box",
      }}
    >
      <h1
  style={{
    fontSize: "clamp(26px, 5vw, 44px)",
    fontWeight: "700",
    marginBottom: "clamp(20px, 4vw, 40px)",
    textAlign: "center",
    letterSpacing: "1px",
  }}
>
  Hydraulics
</h1>

      <div className="hydraulics-grid">
        {/* Product */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Select Product
          </label>
           
          <SearchableSelect
            className="hydraulics-select"
            value={selectedProduct}
            onChange={(val) => {
              setSelectedProduct(val);
              handleProductChange({ target: { value: val } });
            }}
            options={products}
            placeholder="Select Product"
            style={inputStyle}
          />

          <div style={optionButtonRowStyle}>
            <button
              type="button"
              onClick={() =>
                handleAddOption(
                  "products",
                  products,
                  setProducts,
                  "Product"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              style={addOptionButtonStyle}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={() =>
                handleRemoveOption(
                  "products",
                  products,
                  setProducts,
                  selectedProduct,
                  () => {
                    setSelectedProduct("");
                    sessionStorage.removeItem("selectedProduct");
                  },
                  "Product"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
              style={removeOptionButtonStyle}
            >
              REMOVE
            </button>
          </div>

          {/* Shows whatever operation was picked on the Operations page for
              this product, so the selection is visible without having to
              reopen that page. */}
          {productOperation && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                borderRadius: "8px",
                background: "rgba(255,234,0,0.15)",
                border: "1px solid rgba(255,234,0,0.6)",
                fontSize: "clamp(12px, 1.5vw, 14px)",
                fontWeight: "600",
                wordBreak: "break-word",
              }}
            >
              Product Operation: {productOperation}
            </div>
          )}
        </div>

        {/* WFH */}
        <div style={{ width: "100%" }}>
           <label
    style={{
      display: "block",
      height: "auto",
      marginBottom: "10px",
      fontWeight: "700",
      fontSize: "clamp(13px, 1.6vw, 15px)",
    }}
  >
    WFH
  </label>

  <SearchableSelect
    className="hydraulics-select"
    value={selectedWFH}
    onChange={(val) => {
      setSelectedWFH(val);
      handleWFHChange({ target: { value: val } });
    }}
    options={["Harsh"]}
    placeholder="Select User"
    disabled={!!selectedMachine}
    style={{
      ...inputStyle,
      opacity: selectedMachine ? 0.5 : 1,
      cursor: selectedMachine ? "not-allowed" : "pointer",
    }}
  />

  {/* Shows whatever operation was picked on the Operations page for WFH,
      so the selection is visible without having to reopen that page. */}
  {wfhOperation && (
    <div
      style={{
        marginTop: "10px",
        padding: "8px 12px",
        borderRadius: "8px",
        background: "rgba(255,234,0,0.15)",
        border: "1px solid rgba(255,234,0,0.6)",
        fontSize: "clamp(12px, 1.5vw, 14px)",
        fontWeight: "600",
        wordBreak: "break-word",
      }}
    >
      WFH Operation: {wfhOperation}
    </div>
  )}
        </div>

        {/* Person */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Person
          </label>

          <SearchableSelect
    className="hydraulics-select"
    value={selectedPerson}
    onChange={(val) => {
        setSelectedPerson(val);
        handlePersonChange({ target: { value: val } });
    }}
    options={persons}
    placeholder="Select Person"
    style={inputStyle}
  />

          <div style={optionButtonRowStyle}>
            <button
              type="button"
              onClick={() =>
                handleAddOption(
                  "persons",
                  persons,
                  setPersons,
                  "Person"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              style={addOptionButtonStyle}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={() =>
                handleRemoveOption(
                  "persons",
                  persons,
                  setPersons,
                  selectedPerson,
                  () => setSelectedPerson(""),
                  "Person"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
              style={removeOptionButtonStyle}
            >
              REMOVE
            </button>
          </div>
        </div>

        {/* Quantity */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Quantity
          </label>

          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter Quantity"
            style={inputStyle}
          />
        </div>

        {/* Rejection */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Rejection
          </label>

          <input
            type="number"
            value={rejection}
            onChange={(e) => setRejection(e.target.value)}
            placeholder="Enter Rejection"
            style={inputStyle}
          />
        </div>

        {/* Shift Name */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Shift Name
          </label>

          <select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            style={inputStyle}
          >
            <option value="">Select Shift</option>
            {shifts.map((shift) => (
              <option key={shift} value={shift}>
                {shift}
              </option>
            ))}
          </select>

          <div style={optionButtonRowStyle}>
            <button
              type="button"
              onClick={() =>
                handleAddOption(
                  "shifts",
                  shifts,
                  setShifts,
                  "Shift",
                  { sort: false }
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              style={addOptionButtonStyle}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={() =>
                handleRemoveOption(
                  "shifts",
                  shifts,
                  setShifts,
                  selectedShift,
                  () => setSelectedShift(""),
                  "Shift",
                  { sort: false }
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
              style={removeOptionButtonStyle}
            >
              REMOVE
            </button>
          </div>
        </div>

        {/* Start Time */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Start Time
          </label>

          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Stop Time */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Stop Time
          </label>

          <input
            type="time"
            value={stopTime}
            onChange={(e) => setStopTime(e.target.value)}
            style={inputStyle}
          />
        </div>


        {/* Machine */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Machines
          </label>

          <SearchableSelect
    className="hydraulics-select"
    value={selectedMachine}
    onChange={(val) => {
        setSelectedMachine(val);
        handleMachineChange({ target: { value: val } });
    }}
    options={machines}
    placeholder="Select Machine"
    disabled={!!selectedWFH}
    style={{
      ...inputStyle,
      opacity: selectedWFH ? 0.5 : 1,
      cursor: selectedWFH ? "not-allowed" : "pointer",
    }}
  />

          <div style={optionButtonRowStyle}>
            <button
              type="button"
              onClick={() =>
                handleAddOption(
                  "machines",
                  machines,
                  setMachines,
                  "Machine"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#16a34a")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#22c55e")}
              style={addOptionButtonStyle}
            >
              ADD
            </button>
            <button
              type="button"
              onClick={() =>
                handleRemoveOption(
                  "machines",
                  machines,
                  setMachines,
                  selectedMachine,
                  () => setSelectedMachine(""),
                  "Machine"
                )
              }
              onMouseEnter={(e) => (e.currentTarget.style.background = "#dc2626")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#ef4444")}
              style={removeOptionButtonStyle}
            >
              REMOVE
            </button>
          </div>
        </div>

        {/* Other Operation */}
        <div style={{ width: "100%" }}>
          <label
            style={{
              display: "block",
              height: "auto",
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "clamp(13px, 1.6vw, 15px)",
            }}
          >
            Other Operation:
          </label>

          <input
            type="text"
            value={otherOperation}
            onChange={(e) => setOtherOperation(e.target.value)}
            placeholder="Enter Other Operation"
            style={inputStyle}
          />

          <button
            type="button"
            onClick={handleAddOtherOperation}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#16a34a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#22c55e";
            }}
            style={{
              ...inputStyle,
              width: "100%",
              maxWidth: "220px",
              height: "clamp(44px, 6vw, 60px)",
              fontSize: "clamp(13px, 1.8vw, 16px)",
              boxSizing: "border-box",
              marginTop: "12px",
              border: "none",
              background: "#22c55e",
              color: "#fff",
              cursor: "pointer",
              textAlign: "center",
              transition: "background 0.2s ease",
            }}
          >
            ADD
          </button>
        </div>

        <br />
        <br /> 
         <br />
        <br /> 

        <br />
        <br /> 
         <br />
        <br />

        <br />
        <br /> 
         <br />
        <br />



<br />
<br />
<br />
<br />
<br />
<br />
<br />
<br />
<br />
<br />




<div
  style={{
    position: "fixed",
    bottom: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "15px",
    width: "100%",
    maxWidth: "350px",
    padding: "0 20px",
    boxSizing: "border-box",
    zIndex: 1000,
  }}
>
  {/* Submit Button - records the current entry to the shared backend so
      Team Leader / HR can later download everyone's entries together.
      Accessible to every employee. */}
  <button
    onClick={handleSubmit}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "scale(1.02)";
      e.currentTarget.style.boxShadow = "0 0 35px rgba(34,197,94,0.9)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "none";
    }}
    style={{
      width: "100%",
      maxWidth: "320px",
      height: "clamp(56px, 9vw, 80px)",
      fontSize: "clamp(18px, 4vw, 30px)",
      fontWeight: "bold",
      border: "none",
      borderRadius: "12px",
      background: "#22c55e",
      color: "#fff",
      cursor: "pointer",
      transition: "all 0.3s ease",
    }}
  >
    Submit
  </button>

  {/* Download CSV Button - restricted to Team Leader / HR via PIN prompt */}
  <button
    onClick={handleDownloadClick}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "scale(1.02)";
      e.currentTarget.style.boxShadow =
        "0 0 35px rgba(255,234,0,1)";
      e.currentTarget.style.background =
        "linear-gradient(135deg,#ffd60a,#ffea00,#ffee32)";
      e.currentTarget.style.color = "#000";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "scale(1)";
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.background = "#1976d2";
      e.currentTarget.style.color = "#fff";
    }}
    style={{
      width: "100%",
      maxWidth: "320px",
      height: "clamp(56px, 9vw, 80px)",
      fontSize: "clamp(18px, 4vw, 30px)",
      fontWeight: "bold",
      border: "none",
      borderRadius: "12px",
      background: "#1976d2",
      color: "#fff",
      cursor: "pointer",
      transition: "all 0.3s ease",
    }}
  >
    Download CSV
  </button>
</div>   

      </div>
    </div>
  </div>
);
};

export default Hydraulics;